import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import CheckIcon from '@mui/icons-material/Check';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { BuildView } from './buildsApi';
import { STEP_LABELS, statusOf } from './buildMessages';
import { ownBuilds, useBuildsSummary, waitingQuestions } from './buildsSummary';
import { Bar, elapsedOf, monoSx, muted, progressLine, useNow } from './parts';

export const HOME_CARDS_MAX = 3;
const ORDER: Record<string, number> = { running: 0, queued: 1, ready: 2 };

/** The viewer's live builds, as Home lists them: running, then queued, then ready to publish; newest first. */
export function liveBuilds(builds: BuildView[]): BuildView[] {
  return builds.filter((b) => b.state in ORDER)
    .sort((a, b) => ORDER[a.state] - ORDER[b.state] || String(b.created_at).localeCompare(String(a.created_at)));
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** "Step i of m · <the step> · <time>" (the running phase among those the build will run). */
export function stepLine(b: BuildView, elapsed: number | null): string {
  const shown = b.progress.steps.filter((p) => p.state !== 'skipped');
  const at = shown.findIndex((p) => p.state === 'running');
  const step = at >= 0 ? shown[at] : null;
  return [step ? `Step ${at + 1} of ${shown.length}` : null, step ? STEP_LABELS[step.step] || step.step : null,
    progressLine(b, elapsed)].filter(Boolean).join(' · ');
}

function Card({ b, receivedAt, now }: { b: BuildView; receivedAt: number; now: number }) {
  const open = waitingQuestions(b);
  const line = b.state === 'running'
    ? stepLine(b, elapsedOf(b, receivedAt, now)) + (open ? ` · ${plural(open, 'question')} for you` : '')
    : b.state === 'queued' ? statusOf(b).label
    : `Ready to publish${open ? ` · ${plural(open, 'question')} open` : ''}`;
  return <ButtonBase data-testid="home-build" data-job={b.job_id} data-state={b.state}
    onClick={() => useVoyagerStore.getState().navigateToBuilds(b.job_id)}
    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, width: '100%', textAlign: 'left', px: 2, py: 1.5,
      border: '1px solid var(--line)', borderRadius: '12px', bgcolor: 'var(--bg)', '&:hover': { borderColor: 'var(--ink-4)' },
      '&.Mui-focusVisible': { outline: '2px solid var(--acc)', outlineOffset: '2px' } }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'auto minmax(0,1fr)', sm: 'auto minmax(0,1fr) auto' }, gap: 1.5, alignItems: 'center' }}>
      {b.state === 'ready'
        ? <Box component="span" aria-hidden sx={{ width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'var(--ok-bg)', color: 'var(--ok)' }}><CheckIcon sx={{ fontSize: 13 }} /></Box>
        : b.state === 'queued'
          ? <Box component="span" aria-hidden sx={{ width: 12, height: 12, mx: '5px', borderRadius: '50%', border: '2px solid var(--ink-4)' }} />
          : <CircularProgress aria-hidden size={20} thickness={5} sx={{ color: 'var(--acc)', '@media (prefers-reduced-motion: reduce)': { animation: 'none', '& circle': { animation: 'none', strokeDasharray: '40px, 200px' } } }} />}
      <Box sx={{ minWidth: 0 }}>
        <Box component="b" sx={{ display: 'block', fontWeight: 600, fontSize: 14, overflowWrap: 'anywhere' }}>{b.name}</Box>
        <Box component="small" data-testid="home-build-line" sx={{ ...muted, display: 'block' }}>{line}</Box>
        <Box sx={{ ...monoSx, display: { xs: 'block', sm: 'none' } }}>{b.job_id}</Box>
      </Box>
      <Box sx={{ ...monoSx, display: { xs: 'none', sm: 'block' } }}>{b.job_id}</Box>
    </Box>
    {b.state === 'running' && <Bar percent={b.progress.percent} thin />}
  </ButtonBase>;
}

/** Home's block of live builds (#1625 R7): at most three cards, then "All builds (n)"; nothing when there are none. */
export default function BuildCards() {
  const data = useBuildsSummary((s) => s.data);
  const receivedAt = useRef(Date.now());
  const seen = useRef(data);
  if (seen.current !== data) { seen.current = data; receivedAt.current = Date.now(); }
  const live = liveBuilds(ownBuilds(data));
  const now = useNow(live.some((b) => b.state === 'running'));
  if (!live.length) return null;
  return <Box component="section" aria-label="Your builds" data-testid="home-builds" sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
    {live.slice(0, HOME_CARDS_MAX).map((b) => <Card key={b.job_id} b={b} receivedAt={receivedAt.current} now={now} />)}
    {live.length > HOME_CARDS_MAX && <ButtonBase data-testid="home-builds-all" onClick={() => useVoyagerStore.getState().navigateToBuilds(null)}
      sx={{ alignSelf: 'flex-start', fontSize: 13, color: 'var(--ink-2)', textDecoration: 'underline', px: 0.5 }}>All builds ({live.length})</ButtonBase>}
  </Box>;
}
